import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useFranchiseesQuery, useMyContentSubmissionsQuery } from '../api/queries';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../api/client';
import { showToast } from '../components/Toast';
import { contentApi, type SpellCheckResult } from '../api/content';
import type { ContentSubmissionStatus, MyContentSubmission } from '../types';
import { normalizeHeadings } from '../lib/contentHtml';
import ContentBriefSubTab from './content/ContentBriefSubTab';
import OnSiteContentSubTab from './content/OnSiteContentSubTab';
// Editor config is shared with marketing's approval drawer — see quillConfig.
import {
  QUILL_MODULES,
  QUILL_FORMATS,
  QUILL_MODULES_BLOG,
  QUILL_FORMATS_BLOG,
} from '../lib/quillConfig';

type Props = { af: string };
type SubmissionType = 'news_events' | 'blog';

const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * Real image type from the file's first bytes. The name and browser MIME type
 * can't be trusted: phones and Canva often save WebP images with a .png name,
 * and our WordPress sites reject WebP. The server runs the same check.
 */
async function sniffImageType(file: File): Promise<'jpeg' | 'png' | 'webp' | 'other'> {
  const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const ascii = (from: number, to: number) => String.fromCharCode(...b.subarray(from, to));
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  if (b.length >= 8 && [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((v, i) => b[i] === v)) return 'png';
  if (b.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'webp';
  return 'other';
}

const STATUS_LABEL: Record<ContentSubmissionStatus, string> = {
  pending: 'Pending review',
  approved: 'Approved, working on upload.',
  rejected: 'Rejected',
  published: 'Published',
  publish_failed: 'Publish failed',
};

const STATUS_COLOR: Record<ContentSubmissionStatus, string> = {
  pending: '#f59e0b',
  approved: '#3b82f6',
  rejected: '#dc2626',
  published: '#16a34a',
  publish_failed: '#dc2626',
};

function fmtDate(s: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function submissionLabel(s: MyContentSubmission): string {
  if (s.submission_type === 'news_events') return s.news_headline || '(News & Events)';
  if (s.submission_type === 'blog') return s.blog_headline || '(Blog Post)';
  return [s.news_headline, s.blog_headline].filter(Boolean).join(' / ') || '(News + Blog)';
}

/** The original Content tab: franchisees submit news/blog copy for review.
 *  Unchanged — it is now one sub-tab rather than the whole tab. */
function SubmitContentSubTab({ af }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: franchisees } = useFranchiseesQuery();
  const history = useMyContentSubmissionsQuery(af);

  const franchisee = franchisees?.find((f) => f.af_number === af) ?? null;
  const isAdmin = user?.role === 'admin';
  const userOwnsAf = (user?.afNumbers ?? []).includes(af);
  const onBehalf = isAdmin && !userOwnsAf;

  // Form state
  const [submissionType, setSubmissionType] = useState<SubmissionType | ''>('');
  const [newsHeadline, setNewsHeadline] = useState('');
  const [newsContent, setNewsContent] = useState('');
  const [blogHeadline, setBlogHeadline] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogAuthor, setBlogAuthor] = useState('');
  // Whether the user has manually changed Author. Until they do, it mirrors
  // the location's Marketing Name (defaulted in the effect below).
  const [authorEdited, setAuthorEdited] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [imageDescriptions, setImageDescriptions] = useState('');
  // Attestation that a photo/video release exists for everyone pictured.
  // Only meaningful — and only required — when images are attached.
  const [photoReleaseConfirmed, setPhotoReleaseConfirmed] = useState(false);
  // Attestation that the submitter owns or is licensed to use the images
  // themselves. Separate concern from the release above: that one covers the
  // people pictured, this one covers who holds the copyright in the photo.
  const [imageRightsConfirmed, setImageRightsConfirmed] = useState(false);
  const [outboundUrl1, setOutboundUrl1] = useState('');
  const [outboundUrl2, setOutboundUrl2] = useState('');
  const [outboundUrl3, setOutboundUrl3] = useState('');
  const [publishMode, setPublishMode] = useState<'asap' | 'schedule'>('asap');
  const [publishAtLocal, setPublishAtLocal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showBlogGuide, setShowBlogGuide] = useState(false);
  // Blog submitted with no photos: confirm before sending, since the publisher
  // will pick a featured image (site library, else an approved Amada photo).
  const [showNoImageConfirm, setShowNoImageConfirm] = useState(false);

  // Default the blog Author to the franchisee's Marketing Name (location name).
  // Stays editable; once the user edits or clears it, we stop auto-filling.
  useEffect(() => {
    if (!authorEdited) setBlogAuthor(franchisee?.marketing_name ?? '');
  }, [franchisee?.marketing_name, authorEdited]);

  function resetForm() {
    setSubmissionType('');
    setNewsHeadline('');
    setNewsContent('');
    setBlogHeadline('');
    setBlogContent('');
    setBlogAuthor(franchisee?.marketing_name ?? '');
    setAuthorEdited(false);
    setImages([]);
    setFeaturedIndex(0);
    setImageDescriptions('');
    setPhotoReleaseConfirmed(false);
    setImageRightsConfirmed(false);
    setOutboundUrl1('');
    setOutboundUrl2('');
    setOutboundUrl3('');
    setPublishMode('asap');
    setPublishAtLocal('');
  }

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const fileList = input.files;
    if (!fileList) return;
    const incoming = Array.from(fileList);
    // Reset the input so re-selecting the same file fires onChange again
    input.value = '';
    // Combine with already-selected images, dedupe by name+size, cap at MAX
    const existingKey = new Set(images.map((f) => `${f.name}_${f.size}`));
    const additions: File[] = [];
    for (const f of incoming) {
      if (existingKey.has(`${f.name}_${f.size}`)) continue;
      if (f.size > MAX_IMAGE_BYTES) {
        showToast(`${f.name} is over 10 MB and was skipped.`, { variant: 'error' });
        continue;
      }
      const kind = await sniffImageType(f);
      if (kind === 'webp') {
        // WordPress can't use WebP; the server converts it to JPG on submit.
        showToast(
          `${f.name} is a WebP image${/\.webp$/i.test(f.name) ? '' : ' (despite its file name)'} — it will be converted to JPG automatically when you submit.`,
        );
      }
      if (kind === 'other') {
        showToast(`${f.name} is not a JPG, PNG or WebP image and was skipped. Save or export it as JPG and add it again.`, { variant: 'error' });
        continue;
      }
      additions.push(f);
    }
    setImages((prev) => [...prev, ...additions].slice(0, MAX_IMAGES));
  }

  function removeImage(idx: number) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
    // Keep the featured choice pointing at the right image as the list shifts.
    setFeaturedIndex((prev) => {
      if (idx === prev) return 0; // removed the featured one -> default to first
      if (idx < prev) return prev - 1; // items above it shifted down by one
      return prev;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!submissionType) {
      showToast('Choose what kind of content you are submitting.', { variant: 'error' });
      return;
    }
    if (submissionType === 'news_events' && (!newsHeadline.trim() || !stripHtml(newsContent))) {
      showToast('Add a news headline and body before submitting.', { variant: 'error' });
      return;
    }
    if (submissionType === 'blog' && (!blogHeadline.trim() || !stripHtml(blogContent))) {
      showToast('Add a blog headline and body before submitting.', { variant: 'error' });
      return;
    }
    if (images.length > 0 && !photoReleaseConfirmed) {
      showToast(
        'Confirm you have a photo/video release for everyone shown in these photos before submitting.',
        { variant: 'error' },
      );
      return;
    }
    if (images.length > 0 && !imageRightsConfirmed) {
      showToast(
        'Confirm you have the right to use these photos before submitting.',
        { variant: 'error' },
      );
      return;
    }
    if (publishMode === 'schedule') {
      if (!publishAtLocal) {
        showToast('Pick a publish date, or choose "Publish as soon as approved".', { variant: 'error' });
        return;
      }
      const when = new Date(publishAtLocal);
      if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
        showToast('The publish date must be in the future.', { variant: 'error' });
        return;
      }
    }
    if (submissionType === 'blog' && images.length === 0) {
      setShowNoImageConfirm(true);
      return;
    }
    await submitNow();
  }

  async function submitNow() {
    setShowNoImageConfirm(false);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('af_number', af);
      fd.append('submission_type', submissionType);
      if (submissionType === 'news_events') {
        fd.append('news_headline', newsHeadline.trim());
        fd.append('news_content', normalizeHeadings(newsContent));
      } else {
        fd.append('blog_headline', blogHeadline.trim());
        fd.append('blog_content', normalizeHeadings(blogContent));
        if (blogAuthor.trim()) fd.append('blog_author', blogAuthor.trim());
      }
      if (imageDescriptions.trim()) fd.append('image_descriptions', imageDescriptions.trim());
      fd.append('photo_release_confirmed', photoReleaseConfirmed && images.length > 0 ? '1' : '0');
      fd.append('image_rights_confirmed', imageRightsConfirmed && images.length > 0 ? '1' : '0');
      if (outboundUrl1.trim()) fd.append('outbound_url_1', outboundUrl1.trim());
      if (outboundUrl2.trim()) fd.append('outbound_url_2', outboundUrl2.trim());
      if (outboundUrl3.trim()) fd.append('outbound_url_3', outboundUrl3.trim());
      if (publishMode === 'schedule' && publishAtLocal) {
        fd.append('publish_at', new Date(publishAtLocal).toISOString());
      }
      for (const f of images) fd.append('images', f);
      if (submissionType === 'blog' && images.length > 0) {
        fd.append('featured_index', String(featuredIndex));
      }

      const res = await fetch('/api/content/submissions', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let detail = `${res.status} ${res.statusText}`;
        try {
          const body = (await res.json()) as { error?: string };
          if (body.error) detail = body.error;
        } catch {
          // not JSON
        }
        throw new ApiError(detail, res.status);
      }
      showToast('Submitted! Marketing will review and publish.', { variant: 'success' });
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ['content-my-submissions', af] });
    } catch (err) {
      showToast(`Submit failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="content-tab" style={{ maxWidth: 900, margin: '0 auto' }}>
      {onBehalf && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
            padding: '10px 14px',
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          <strong>Submitting on behalf of {franchisee?.marketing_name ?? af}.</strong>{' '}
          The submission will be recorded under your account, with this AF flagged.
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
            Submit Content for Your Site
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
            Submit news and events or blog content for publication on your franchisee microsite.
            Marketing will review before it goes live.
          </p>
        </div>

        <Field label="What are you submitting?" required>
          <div style={{ display: 'flex', gap: 16, fontSize: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name="submission_type"
                checked={submissionType === 'news_events'}
                onChange={() => setSubmissionType('news_events')}
              />
              News &amp; Events
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name="submission_type"
                checked={submissionType === 'blog'}
                onChange={() => setSubmissionType('blog')}
              />
              Blog Post
            </label>
          </div>
        </Field>

        {!submissionType && (
          <div
            style={{
              border: '1px dashed #cbd5e1',
              borderRadius: 8,
              padding: '48px 24px',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: 14,
              minHeight: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Choose an option above to start your submission.
          </div>
        )}

        {submissionType === 'news_events' && (
          <Section title="News & Events">
            <Field label="News Headline" hint="A short headline (max 100 characters) — used as the post title." required>
              <input
                type="text"
                value={newsHeadline}
                onChange={(e) => setNewsHeadline(e.target.value.slice(0, 100))}
                style={textInputStyle}
                placeholder="e.g., Amada Dallas Honored as Best of Home Care 2026"
                required
              />
            </Field>
            <Field label="News Content" hint="Use the toolbar for headings, bold, italic, links, and lists." required>
              <ReactQuill
                value={newsContent}
                onChange={setNewsContent}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                style={{ background: '#fff' }}
                theme="snow"
              />
              <SubmitterSpellCheck value={newsContent} onApply={setNewsContent} />
            </Field>
          </Section>
        )}

        {submissionType === 'blog' && (
          <>
            <button
              type="button"
              onClick={() => setShowBlogGuide(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: 'none',
                padding: 0,
                margin: '0 0 8px',
                color: '#2563eb',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                textAlign: 'left',
              }}
            >
              📘 How to Write a Good Blog (And What &ldquo;Good&rdquo; Actually Means)
            </button>
            <Section title="Blog Post">
            <Field label="Blog Headline" hint="A short headline (max 100 characters)." required>
              <input
                type="text"
                value={blogHeadline}
                onChange={(e) => setBlogHeadline(e.target.value.slice(0, 100))}
                style={textInputStyle}
                placeholder="e.g., 5 Tips for Helping Aging Parents Stay Active"
                required
              />
            </Field>
            <Field
              label="Author"
              hint="Defaults to your location name. Edit it, or clear it if you'd rather not show an author."
            >
              <input
                type="text"
                value={blogAuthor}
                onChange={(e) => {
                  setBlogAuthor(e.target.value.slice(0, 255));
                  setAuthorEdited(true);
                }}
                style={textInputStyle}
                placeholder="e.g., Jane Smith"
              />
            </Field>
            <Field
              label="Blog Content"
              hint="Use the toolbar for headings, bold, italic, links, lists, and to embed a YouTube, X, Instagram, Vimeo, or TikTok link."
              required
            >
              <ReactQuill
                value={blogContent}
                onChange={setBlogContent}
                modules={QUILL_MODULES_BLOG}
                formats={QUILL_FORMATS_BLOG}
                style={{ background: '#fff' }}
                theme="snow"
              />
              <SubmitterSpellCheck value={blogContent} onApply={setBlogContent} />
            </Field>
            </Section>
            {showBlogGuide && <BlogGuideModal onClose={() => setShowBlogGuide(false)} />}
            {showNoImageConfirm && (
              <NoImageConfirmModal
                onBack={() => setShowNoImageConfirm(false)}
                onContinue={() => void submitNow()}
              />
            )}
          </>
        )}

        {submissionType && (
          <Section title="Images" subtitle="Optional. Upload up to 6 images to accompany your submission.">
            <div>
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                onChange={handleImageChange}
                disabled={images.length >= MAX_IMAGES}
                style={{ marginBottom: 8 }}
              />
              <div style={{ fontSize: 13, color: '#64748b' }}>
                <strong>Accepted file types: JPG, PNG and WebP</strong> — up to 10 MB each, {MAX_IMAGES} max. ({images.length}/{MAX_IMAGES} selected)
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                WebP images are converted to JPG automatically. HEIC (iPhone) and other formats aren't accepted — save or
                export those as JPG first.
              </div>
              {images.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))',
                    gap: 10,
                    marginTop: 12,
                  }}
                >
                  {images.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        padding: 6,
                        position: 'relative',
                      }}
                    >
                      <img
                        src={URL.createObjectURL(f)}
                        alt=""
                        style={{
                          width: '100%',
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 4,
                          display: 'block',
                        }}
                      />
                      <div
                        style={{
                          fontSize: 11,
                          color: '#64748b',
                          marginTop: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={f.name}
                      >
                        {f.name}
                      </div>
                      {submissionType === 'blog' && (
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12,
                            marginTop: 4,
                            cursor: 'pointer',
                            color: featuredIndex === i ? '#16a34a' : '#64748b',
                            fontWeight: featuredIndex === i ? 600 : 400,
                          }}
                        >
                          <input
                            type="radio"
                            name="featuredImage"
                            checked={featuredIndex === i}
                            onChange={() => setFeaturedIndex(i)}
                          />
                          {featuredIndex === i ? '★ Featured image' : 'Set as featured'}
                        </label>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        title="Remove"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          width: 22,
                          height: 22,
                          fontSize: 14,
                          cursor: 'pointer',
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Field
              label="Briefly describe what's in each photo"
              hint="Optional but strongly recommended. A sentence per photo. We'll auto-generate proper alt text and image descriptions for SEO and accessibility."
            >
              <textarea
                value={imageDescriptions}
                onChange={(e) => setImageDescriptions(e.target.value)}
                rows={4}
                placeholder={`e.g.\n1) Jim Smith presenting a CEU at Hoag Hospital in Newport Beach.\n2) Sally speaking to senior policyholders at a long-term care insurance talk.\n3) Team Amada Atlanta at The Walk to End Alzheimer's.`}
                style={textInputStyle}
              />
            </Field>
            {images.length > 0 && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '12px 14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  background: '#f8fafc',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={photoReleaseConfirmed}
                  onChange={(e) => setPhotoReleaseConfirmed(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I have obtained a photo/video release form for all the individuals shown in these
                  photos <span style={{ color: '#dc2626' }}>*</span>
                  <br />
                  <span style={{ fontSize: 13, color: '#64748b' }}>
                    For a copy of AFI's photo/video release form,{' '}
                    <a
                      href="/static/forms/AFI-Photo-Video-Release.docx"
                      download
                      // The label wraps the checkbox, so a bare click here would
                      // toggle it. Stop the click from reaching the label.
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                    >
                      click here
                    </a>
                    .
                  </span>
                </span>
              </label>
            )}
            {images.length > 0 && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '12px 14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  background: '#f8fafc',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={imageRightsConfirmed}
                  onChange={(e) => setImageRightsConfirmed(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  I have the right to use every photo in this submission — they are my own, or
                  licensed for commercial use. None of them were taken from Google Images, social
                  media, or another website. <span style={{ color: '#dc2626' }}>*</span>
                  <br />
                  <span style={{ fontSize: 13, color: '#64748b' }}>
                    Photos found online are almost always copyrighted, and a copyright claim is made
                    against you personally — not against Amada Franchise, Inc. Need images?{' '}
                    <a
                      href="https://www.canva.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      // The label wraps the checkbox, so a bare click here would
                      // toggle it. Stop the click from reaching the label.
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                    >
                      Canva
                    </a>{' '}
                    and AFI's photo library are both licensed for commercial use, so anything from
                    them is safe to publish.
                  </span>
                </span>
              </label>
            )}
          </Section>
        )}

        {submissionType && (
          <Section
            title="Related Links (Optional)"
            subtitle={
              <>
                Add up to 3 reputable web links related to your story. These help search engines see your news section as authoritative — Google rewards content that cites trusted sources.
                <br />
                <strong>Good examples:</strong> Senior care publications (AARP, McKnight's Home Care), health/aging organizations (CDC aging resources, Alzheimer's Association), social media posts from event co-hosts, or websites of partner organizations. We'll automatically link each URL to the most relevant words in your article.
              </>
            }
          >
            <Field label="URL 1">
              <input
                type="url"
                value={outboundUrl1}
                onChange={(e) => setOutboundUrl1(e.target.value)}
                placeholder="https://..."
                style={textInputStyle}
              />
            </Field>
            <Field label="URL 2">
              <input
                type="url"
                value={outboundUrl2}
                onChange={(e) => setOutboundUrl2(e.target.value)}
                placeholder="https://..."
                style={textInputStyle}
              />
            </Field>
            <Field label="URL 3">
              <input
                type="url"
                value={outboundUrl3}
                onChange={(e) => setOutboundUrl3(e.target.value)}
                placeholder="https://..."
                style={textInputStyle}
              />
            </Field>
          </Section>
        )}

        {submissionType && (
          <Section
            title="Publishing schedule"
            subtitle="Publish as soon as it's approved, or schedule it for a future date."
          >
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 20 }}>
              <input
                type="radio"
                name="publishMode"
                checked={publishMode === 'asap'}
                onChange={() => setPublishMode('asap')}
              />
              Publish as soon as approved
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input
                type="radio"
                name="publishMode"
                checked={publishMode === 'schedule'}
                onChange={() => setPublishMode('schedule')}
              />
              Schedule for a date
            </label>
            {publishMode === 'schedule' && (
              <div style={{ marginTop: 10 }}>
                <input
                  type="datetime-local"
                  value={publishAtLocal}
                  onChange={(e) => setPublishAtLocal(e.target.value)}
                  style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 14 }}
                />
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                  It still needs review first — it will publish on this date once approved.
                </div>
              </div>
            )}
          </Section>
        )}

        {submissionType && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '12px 24px',
                background: submitting ? '#94a3b8' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 15,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit for Review'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              disabled={submitting}
              style={{
                padding: '12px 18px',
                background: '#fff',
                color: '#64748b',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Clear form
            </button>
          </div>
        )}
      </form>

      <div style={{ marginTop: 36 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
          Your recent submissions
        </h3>

        {history.isLoading && <div style={{ color: '#64748b', fontSize: 14 }}>Loading…</div>}

        {history.data && history.data.submissions.length === 0 && (
          <div style={{ color: '#64748b', fontSize: 14, padding: '12px 0' }}>
            No submissions yet for this site.
          </div>
        )}

        {history.data && history.data.submissions.length > 0 && (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Submitted</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Headline</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {history.data.submissions.map((s) => {
                  // A submission stays "Scheduled" until its publish date actually
                  // arrives — including after the publish job has handed it to
                  // WordPress as a future post. It used to flip to "Published" the
                  // moment the job ran, which is why a franchisee who scheduled
                  // four posts a week apart saw all four report as published the
                  // same afternoon.
                  const isScheduled =
                    !!s.publish_at &&
                    new Date(s.publish_at).getTime() > Date.now() &&
                    (s.status === 'pending' || s.status === 'approved' || s.status === 'published');
                  return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      {fmtDate(s.created_at)}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      {s.submission_type === 'news_events'
                        ? 'News & Events'
                        : s.submission_type === 'blog'
                          ? 'Blog'
                          : 'News + Blog'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {s.status === 'published' && s.published_url && !isScheduled ? (
                        <a
                          href={s.published_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'none' }}
                        >
                          {submissionLabel(s)}
                          <span style={{ marginLeft: 4, fontSize: 11, color: '#94a3b8' }}>↗</span>
                        </a>
                      ) : (
                        submissionLabel(s)
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 12,
                          background: (isScheduled ? '#7c3aed' : STATUS_COLOR[s.status]) + '22',
                          color: isScheduled ? '#7c3aed' : STATUS_COLOR[s.status],
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        {isScheduled ? 'Scheduled' : STATUS_LABEL[s.status]}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 13 }}>
                      {isScheduled
                        ? `Scheduled for ${fmtDate(s.publish_at)} · ${
                            s.status === 'published'
                              ? 'Queued on your site'
                              : s.status === 'approved'
                                ? 'Reviewed'
                                : 'Pending review'
                          }`
                        : s.status === 'rejected' && s.rejection_reason
                          ? `Rejected: ${s.rejection_reason}`
                          : s.status === 'published' && s.published_at
                            ? `Published ${fmtDate(s.published_at)}`
                            : s.status === 'publish_failed' && s.publish_error
                              ? `Failed: ${s.publish_error}`
                              : ''}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const textInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
        {label}
        {required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
      </label>
      {hint && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{hint}</div>
      )}
      {children}
    </div>
  );
}

const BLOG_GUIDE_HTML = `<p>A good blog is not just a long article. A good blog helps readers solve a problem, answers a question, and shows why your organization is qualified to provide that information.</p>
<p>When creating blog content, focus on improving your <strong>E-E-A-T</strong>:</p>
<ul>
<li><strong>Experience</strong> &ndash; What real-world experience do you have?</li>
<li><strong>Expertise</strong> &ndash; What professional knowledge can you share?</li>
<li><strong>Authoritativeness</strong> &ndash; Why should people trust your advice?</li>
<li><strong>Trustworthiness</strong> &ndash; Is your information accurate, honest, and helpful?</li>
</ul>
<h3>Write About What You Know</h3>
<p>The easiest way to improve E-E-A-T is to write about topics where your team has direct experience. Ask yourself:</p>
<ul>
<li>What do we help clients with every day?</li>
<li>What problems do we solve?</li>
<li>What questions do clients ask most often?</li>
<li>What certifications, training, or licenses do we have?</li>
<li>What unique experience does our staff bring?</li>
</ul>
<p>Good blogs share insights that only someone in your position would know.</p>
<h4>Example</h4>
<p>Instead of writing: <em>&ldquo;Why Eye Care Matters&rdquo;</em></p>
<p>Write: <em>&ldquo;How Harsh Sun Exposure in Arizona Affects Eye Health and What Our Care Team Recommends&rdquo;</em></p>
<p>The second topic demonstrates local knowledge, real-world experience, professional expertise, and relevance to your audience.</p>
<h3>Use Your Location as an Advantage</h3>
<p>Google values content that is relevant to the community being served. Consider:</p>
<ul>
<li>Local weather conditions</li>
<li>Seasonal health concerns</li>
<li>Regional trends</li>
<li>Community resources</li>
<li>State-specific regulations</li>
<li>Challenges your clients face in your area</li>
</ul>
<p>Examples: how summer heat impacts seniors; fall prevention during icy winters; allergy season and respiratory health; transportation resources for local families; community programs available in your city.</p>
<h3>Highlight Your Background</h3>
<p>Do not be afraid to showcase your qualifications. Include certifications, professional licenses, years of experience, specialized training, industry awards, and community involvement. Readers want to know who is providing the information and why they should trust it.</p>
<h3>Use Real Quotes Whenever Possible</h3>
<p>One of the best ways to strengthen E-E-A-T is by including quotes from people with real experience. Consider adding quotes from caregivers, nurses, therapists, physicians, clients (with permission), family members (with permission), community partners, and other trusted experts. Quotes help readers see that the information comes from real people, not just a website.</p>
<h4>Why Quotes Matter</h4>
<p>Quotes can demonstrate first-hand experience, build trust with readers, add credibility to your content, make articles feel more personal and authentic, and support claims with expert insight.</p>
<h4>Example</h4>
<p>Instead of writing: <em>&ldquo;Staying hydrated during the summer is important for seniors.&rdquo;</em></p>
<p>Write: <em>&ldquo;During extreme heat, many seniors don&rsquo;t realize they&rsquo;re becoming dehydrated until symptoms appear. We encourage clients to drink water throughout the day, even if they don&rsquo;t feel thirsty.&rdquo; &mdash; Jane Smith, RN</em></p>
<p>Or: <em>&ldquo;Having a caregiver remind my mother to drink water and stay indoors during heat waves gave our family peace of mind.&rdquo; &mdash; Family Member</em></p>
<h4>Best Practices</h4>
<ul>
<li>Use real names and credentials whenever permission is granted.</li>
<li>Include certifications or professional titles when relevant.</li>
<li>Keep quotes short and easy to understand.</li>
<li>Make sure quotes add unique insight rather than repeating information already in the article.</li>
<li>Never create fake testimonials or expert quotes.</li>
</ul>
<p>Whenever possible, include at least one expert or first-hand quote in each blog. Real experiences and professional insights are often more persuasive and trustworthy than generic content alone.</p>
<h3>Follow Basic SEO Rules</h3>
<p><strong>Use a clear title</strong> that answers a question or solves a problem &mdash; e.g. &ldquo;7 Signs Your Loved One May Need Home Care,&rdquo; &ldquo;How to Prevent Falls at Home,&rdquo; &ldquo;What Families Should Know About Memory Care.&rdquo;</p>
<p><strong>Use headings</strong>: H2 for major topics, H3 for supporting details. This improves readability and helps search engines understand the page.</p>
<p><strong>Include relevant keywords</strong> your audience would actually search for &mdash; but do not stuff them. Write for people first, search engines second.</p>
<p><strong>Answer questions directly</strong>: readers scan, so provide clear answers early.</p>
<p><strong>Link to helpful resources</strong> when appropriate: related blogs, service pages, and trusted external resources.</p>
<p><strong>Write at a simple reading level</strong>: aim for a 5th-grade level, short sentences, simple words, clear explanations. Only go higher if your audience expects more technical language. Simple writing is not weak writing &mdash; it is effective writing.</p>
<p><strong>Use bullet points</strong> to highlight key takeaways, list benefits, explain steps, and summarize information. Avoid large blocks of text.</p>
<h3>Structure Every Blog Like This</h3>
<ul>
<li>Clear title</li>
<li>Short introduction</li>
<li>Main problem or question</li>
<li>Helpful information and expert insights</li>
<li>Practical tips</li>
<li>Conclusion</li>
<li>Call to action</li>
</ul>
<h3>Final Test</h3>
<p>Before publishing, ask:</p>
<ul>
<li>Does this help a real client?</li>
<li>Does it demonstrate our experience?</li>
<li>Does it show our expertise?</li>
<li>Is it relevant to our location or audience?</li>
<li>Is it easy to read?</li>
<li>Is it organized with headings and bullet points?</li>
<li>Would someone trust this information?</li>
</ul>
<p>If the answer is yes, you likely have a strong blog that supports both SEO and E-E-A-T.</p>
<h3>How Often Should You Post?</h3>
<p>The best publishing schedule is the one you can maintain consistently. Once a week is great; once a month is great; once a quarter is still valuable. Consistency matters more than volume.</p>
<p>Many organizations publish a large number of blogs at once and then go silent for months. That can create a temporary spike, but it rarely helps long-term growth. Search engines want to see a site that is active, relevant, consistently updated, and focused on its area of expertise. A steady schedule sends stronger long-term signals than occasional bursts.</p>
<h3>Focus on Quality Over Quantity</h3>
<p>One high-quality blog each month is often more valuable than several rushed articles in a single week. Before publishing, make sure each blog demonstrates real experience and expertise, answers a client&rsquo;s question, provides useful information, is relevant to your services and location, and is easy to read.</p>
<p>The goal is not simply to publish more content &mdash; it is to build a library of helpful resources that shows search engines and potential clients that you are an active, trusted authority in your field. If you are choosing between publishing more often or publishing better content, choose better content every time.</p>`;

function NoImageConfirmModal({ onBack, onContinue }: { onBack: () => void; onContinue: () => void }) {
  return (
    <div
      onClick={onBack}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="no-image-title"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 8, maxWidth: 480, width: '100%', padding: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
      >
        <h2 id="no-image-title" style={{ margin: '0 0 10px', fontSize: 18, color: '#0f172a' }}>
          No photo uploaded
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.5, color: '#334155' }}>
          If you submit without a photo, your blog post will be published with an image we choose for you: a
          suitable photo from your site&rsquo;s library, or one of Amada&rsquo;s approved photos. Go back to
          upload your own photo, or submit anyway.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onBack}
            autoFocus
            style={{ padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
          >
            Go back and add a photo
          </button>
          <button
            type="button"
            onClick={onContinue}
            style={{ padding: '10px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}
          >
            Submit without a photo
          </button>
        </div>
      </div>
    </div>
  );
}

function BlogGuideModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 8,
          maxWidth: 720,
          width: '100%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>
            How to Write a Good Blog (And What &ldquo;Good&rdquo; Actually Means)
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#64748b' }}
          >
            ×
          </button>
        </div>
        <div
          style={{ padding: '4px 20px 16px', overflowY: 'auto', fontSize: 14, lineHeight: 1.6, color: '#334155' }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: BLOG_GUIDE_HTML }}
        />
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer' }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <fieldset
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: 16,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <legend style={{ fontWeight: 700, fontSize: 14, padding: '0 6px' }}>{title}</legend>
      {subtitle && <div style={{ fontSize: 13, color: '#64748b' }}>{subtitle}</div>}
      {children}
    </fieldset>
  );
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ---------------------------------------------------------------------------
// SubmitterSpellCheck — runs a conservative Haiku spell/grammar pass on the
// current body HTML and lets the submitter accept the corrections before
// submitting. Renders directly under the Quill editor.
// ---------------------------------------------------------------------------
function SubmitterSpellCheck(props: {
  value: string;
  onApply: (corrected: string) => void;
}) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'result'; result: SpellCheckResult }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  async function runCheck() {
    if (!stripHtml(props.value)) {
      showToast('Write some content first.', { variant: 'error' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const result = await contentApi.spellcheck(props.value);
      setState({ kind: 'result', result });
    } catch (err) {
      setState({ kind: 'error', message: (err as Error).message });
    }
  }

  function acceptAll() {
    if (state.kind !== 'result') return;
    props.onApply(state.result.correctedHtml);
    showToast(`Applied ${state.result.fixes.length} correction${state.result.fixes.length === 1 ? '' : 's'}.`, { variant: 'success' });
    setState({ kind: 'idle' });
  }

  return (
    <div style={{ marginTop: 8 }}>
      {state.kind === 'idle' && (
        <button
          type="button"
          onClick={runCheck}
          style={{
            padding: '6px 12px',
            background: '#fff',
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            fontSize: 13,
            cursor: 'pointer',
            color: '#475569',
          }}
        >
          Check spelling &amp; grammar
        </button>
      )}
      {state.kind === 'loading' && (
        <div style={{ fontSize: 13, color: '#64748b' }}>Checking…</div>
      )}
      {state.kind === 'error' && (
        <div style={{ fontSize: 13, color: '#dc2626' }}>
          Check failed: {state.message}{' '}
          <button
            type="button"
            onClick={() => setState({ kind: 'idle' })}
            style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: 13 }}
          >
            Try again
          </button>
        </div>
      )}
      {state.kind === 'result' && !state.result.hasChanges && (
        <div
          style={{
            fontSize: 13,
            color: '#16a34a',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            padding: '6px 10px',
            borderRadius: 4,
            display: 'inline-block',
          }}
        >
          ✓ No spelling or grammar issues found
        </div>
      )}
      {state.kind === 'result' && state.result.hasChanges && (
        <div
          style={{
            border: '1px solid #fde68a',
            background: '#fffbeb',
            padding: 12,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ color: '#92400e' }}>
              {state.result.fixes.length} suggestion{state.result.fixes.length === 1 ? '' : 's'} found
            </strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={acceptAll}
                style={{
                  padding: '4px 10px',
                  background: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Apply all
              </button>
              <button
                type="button"
                onClick={() => setState({ kind: 'idle' })}
                style={{
                  padding: '4px 10px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: 4,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {state.result.fixes.map((f, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                <span style={{ background: '#fee2e2', textDecoration: 'line-through', padding: '0 3px' }}>
                  {f.original}
                </span>{' '}
                →{' '}
                <span style={{ background: '#dcfce7', padding: '0 3px' }}>{f.corrected}</span>
                <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>({f.reason})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// ── Tab shell ───────────────────────────────────────────────────────────────

type ContentSubTabId = 'content-submit' | 'content-brief' | 'content-on-site';

const CONTENT_SUBS: ReadonlyArray<{
  id: ContentSubTabId;
  label: string;
  adminOnly?: boolean;
}> = [
  { id: 'content-submit', label: 'Submit Content' },
  { id: 'content-brief', label: 'Territory Brief' },
  // Internal SEO tooling — franchisees and FBCs never see this. The API is
  // gated on the server too (requireRole('admin')); this only hides the entry.
  { id: 'content-on-site', label: 'On Site Content', adminOnly: true },
];

export default function ContentTab({ af }: Props) {
  const [sub, setSub] = useState<ContentSubTabId>('content-submit');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const subs = CONTENT_SUBS.filter((s) => !s.adminOnly || isAdmin);

  return (
    <>
      <div className="perf-sub-tabs">
        {subs.map((s, i) => (
          <span key={s.id} style={{ display: 'contents' }}>
            {/* Divider marking the start of the admin-only tools, so the
                boundary between franchisee-facing and internal tabs reads at a
                glance. Only shown when a franchisee-facing tab precedes it. */}
            {s.adminOnly && i > 0 && !subs[i - 1].adminOnly && (
              <span className="perf-sub-divider" aria-hidden="true" />
            )}
            <button
              className={`perf-sub-btn${sub === s.id ? ' active' : ''}${
                s.adminOnly ? ' admin-only' : ''
              }`}
              onClick={() => setSub(s.id)}
            >
              {s.label}
            </button>
          </span>
        ))}
      </div>

      {sub === 'content-submit' && <SubmitContentSubTab af={af} />}
      {sub === 'content-brief' && <ContentBriefSubTab af={af} />}
      {sub === 'content-on-site' && isAdmin && <OnSiteContentSubTab af={af} />}
    </>
  );
}
